/* @flow */
import React from 'react';

import Avatar from 'entities/Avatar';
import BoxBody from 'components/BoxBody.jsx';
import Button from 'components/Button.jsx';
import ConfirmDialog from 'components/ConfirmDialog.jsx';
import Divider from 'components/Divider.jsx';
import Group from 'entities/Group';
import GroupActions from 'groups/actions/GroupActions';
import Link from 'components/Link.jsx';
import List from 'components/List.jsx';
import MediaObject from 'components/MediaObject.jsx';
import ModalTrigger from 'components/ModalTrigger.jsx';
import ToastActions from 'actions/ToastActions';
import { mountModal } from 'lib/modal.js';

import GroupAvatarSelector from './GroupAvatarSelector.jsx';
import GroupForm from './GroupForm.jsx';
import GroupOptionsForm from './GroupOptionsForm.jsx';
import ReuseClassConfirmDialog from './ReuseClassConfirmDialog.jsx';
import RemoveClassConfirmationDialog from './RemoveClassConfirmDialog.jsx';

import './styles/GroupEditPanel.scss';

type Props = {
  group: Group,
  onCodeChange: Function,
  userIsTeacher: boolean,
  onRequestHide: () => any,
};

type State = {
  name: ?string,
  code: ?string,
  canSubmit: boolean,
  group: Group,
  initialCode?: string,
  organizationId?: ?string,
};

export default class GroupEditPanel extends React.Component {
  props: Props;
  state: State = {
    name: null,
    code: null,
    canSubmit: false,
    group: this.props.group,
  };

  static contextTypes = {
    router: React.PropTypes.object.isRequired,
  };

  componentWillMount() {
    const { group } = this.props;
    this.setState({ initialCode: group.getCode() });
  }

  onAvatarSelected = (avatar: Avatar) => {
    GroupActions.patch(this.props.group, {
      group_avatar_id: avatar.id,
    }, 'Icon saved');
  };

  onInvalidChange = () => {
    this.setState({ name: null, code: null, canSubmit: false });
  };

  /* eslint-disable flowtype/space-before-type-colon*/
  onValidChange = (name: string, code: string, organizationId?: ?string) => {
    this.setState({ name, code, organizationId, canSubmit: true });
  };

  onOptionsChange = (name: string, value: boolean) => {
    const underscored = name.replace(/([A-Z])/g, a => `_${a.toLowerCase()}`);
    GroupActions.patch(this.props.group, { [underscored]: value })
      .then(
        group => this.setState(group),
        ({ message }) => {
          this.setState({ group: this.props.group });
          ToastActions.showErrorToast(message);
        });
  };

  onSubmit = () => {
    const { code, name, organizationId } = this.state;

    const attributes = {
      name: code,
      class_name: name,
      group_avatar_id: this.refs.avatarSelector.getSelectedAvatarId(),
      organization_id: organizationId,
    };

    const update = () => {
      GroupActions.patch(this.props.group, attributes).then(() => {
        this.props.onRequestHide();
        if (this.state.initialCode !== code) {
          this.props.onCodeChange(code);
        }
      }, (error) => ToastActions.showAPIError(error));
    };

    if (attributes.name === this.props.group.getCode()) {
      update();
    } else {
      mountModal(this.renderConfirmDialog(
        `Are you sure you want to change your class code to @${attributes.name}`,
        'Current participants won’t be affected, but new participants ' +
        'must use this code to join your class.',
        'No',
        'Yes',
        update
      ), this);
    }
  };

  renderConfirmDialog(title: string, note: string, cancelButtonText: string, actionButtonText: string, onConfirm: Function) {
    return (
      <ConfirmDialog
        eventScreenName="GroupEditPanel"
        glyph="caution"
        glyphColor="lava"
        title={title}
        actionButtonText={actionButtonText}
        cancelButtonText={cancelButtonText}
        onConfirm={onConfirm}
        subtext={note}
      />
    );
  }

  handleRemoveCompletion = () => {
    this.context.router.push('/classes');
  };

  renderRemoveDialog() {
    return (
      <RemoveClassConfirmationDialog
        group={this.props.group}
        onSubmit={() => this.props.onRequestHide()}
        onActionCompletion={() => this.handleRemoveCompletion()}
      />
    );
  }

  renderRemoveClassButton() {
    if (this.props.group.canEdit()) {
      return (
        <ModalTrigger modal={this.renderRemoveDialog()}>
          <div>
            <Link color="lava" block>
              Remove from class list
            </Link>
            <div className="GroupEditPanel-subtext">
              This class will be archived and will no longer appear in your class list.
            </div>
          </div>
        </ModalTrigger>
      );
    }
    return null;
  }

  handleReuseCompletion = (newGroup: Group) => {
    this.context.router.push('/classes/' + newGroup.getCode());
  };

  renderReuseDialog() {
    const subText = `A new class will be created with the code @${this.props.group.getCode()}. You can always view and restore archived classes from your settings.`;
    return (
      <ReuseClassConfirmDialog
        group={this.props.group}
        onSubmit={() => this.props.onRequestHide()}
        onActionCompletion={newGroup => this.handleReuseCompletion(newGroup)}
        subText={subText}
        archiveFirst
      />
    );
  }

  renderReuseClassButton() {
    if (this.props.group.canEdit()) {
      return (
        <ModalTrigger modal={this.renderReuseDialog()}>
          <div>
            <Link color="lava" block>
              Reuse class code
            </Link>
            <div className="GroupEditPanel-subtext">
              This class will be archived, and a new class will be created with this code.
            </div>
          </div>
        </ModalTrigger>
      );
    }
    return null;
  }

  render() {
    const reuseClassButton = this.renderReuseClassButton();
    const removeClassButton = this.renderRemoveClassButton();
    const archiveSection = (removeClassButton || reuseClassButton) && (
      <div>
        <Divider size="medium" />
        <List size="small" className="archiveSection">
          {reuseClassButton}
          {removeClassButton}
        </List>
      </div>
    );

    return (
      <BoxBody className="GroupEditPanel">
        <MediaObject>
          <div className="GroupEditPanel-avatar">
            <GroupAvatarSelector
              ref="avatarSelector"
              onSelect={this.onAvatarSelected}
              initialAvatar={this.props.group.getAvatar()}
            />
          </div>
          <div>
            <GroupForm
              group={this.state.group}
              name={this.props.group.getName()}
              code={this.props.group.getCode()}
              currentOrganization={this.props.group.getOrganization()}
              onFormValid={this.onValidChange}
              onFormInvalid={this.onInvalidChange}
            />
            <Button
              className="GroupEditPanel-submit"
              disabled={!this.state.canSubmit}
              onClick={this.onSubmit}
              eventElementName="GroupEditPanel-submit">
              Save
            </Button>
          </div>
        </MediaObject>

        <div>
          <Divider size="medium" />
          <GroupOptionsForm
            hasChildren={this.props.group.hasChildren()}
            hideUnderageNote={!this.props.group.getOrganization()}
            searchable={this.props.group.isSearchable()}
            showSearchable={!this.props.group.hasStudentOrParentOwner()}
            subscriberInitiatedChats={this.props.group.canSubscribersInitiateChats()}
            userIsTeacher={this.props.userIsTeacher}
            onChange={this.onOptionsChange}
          />
        </div>

        {archiveSection}
      </BoxBody>
    );
  }
}



// WEBPACK FOOTER //
// ./src/groups/components/GroupEditPanel.jsx